import React, { useEffect, useState, useRef } from 'react';
import { Section } from '../components/layout';
import { useTranslation } from 'react-i18next';
import { useStateContext } from '../context/ContextProvider';
import { useDispatch, useSelector } from 'react-redux';
import { getAllReviews } from '../redux/actions/reviews';
import { Loader, ReviewsCard } from '../components/utils';

import { motion, useInView } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/effect-cards';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import { EffectCards, Pagination, Navigation, Autoplay } from 'swiper/modules';
import { FiStar, FiChevronLeft, FiChevronRight, FiMessageCircle } from 'react-icons/fi';
import { media } from '../server';

function Reviews() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { currentDir } = useStateContext();
  const { isLoading, AllReviews, pagination } = useSelector((state) => state.Reviews);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.2 });

  useEffect(() => {
    dispatch(getAllReviews(page, limit));
  }, [page, limit, dispatch]);

  // Calculate average rating
  const averageRating = 4.8;
  const totalReviews = "50K";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const headerVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  return (
    <div ref={sectionRef} className="relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 bg-gradient-to-br from-color-1/5 via-transparent to-color-2/5 pointer-events-none" />
      <div className="absolute top-0 right-0 w-96 h-96 bg-color-1/10 rounded-full filter blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-color-2/10 rounded-full filter blur-3xl pointer-events-none" />
      
      {isLoading ? (
        <Loader />
      ) : (
        <Section className="relative z-10 py-20 lg:py-32">
          <div className="container mx-auto px-4">
            {/* Header Section with Stats */}
            <motion.div
              variants={headerVariants}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              className="text-center mb-16"
            >
              {/* Badge */}
              <motion.div
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : { scale: 0 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-color-1/10 rounded-full mb-6"
              >
                <FiMessageCircle className="text-color-1" size={16} />
                <span className="text-sm font-kufi font-light text-color-1">
                  {t("client testimonials")}
                </span>
              </motion.div>

              {/* Title */}
              <h2   className="text-4xl md:text-5xl lg:text-6xl  font-kufi font-bold mb-4 bg-gradient-to-r from-color-1 to-color-2 bg-clip-text text-transparent pb-6">
                {t("What Our Customers Say")}
              </h2>
              
              {/* Subtitle */}
              <p className="body-1 text-n-5 dark:text-n-4 max-w-2xl mx-auto mb-8">
                {t("Don't just take our word for it - hear from our satisfied customers")}
              </p>

              {/* Stats Cards */}
              <div className="flex flex-wrap justify-center gap-8 mt-8">
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  transition={{ delay: 0.3, type: "spring" }}
                  className="text-center"
                >
                  <div className="text-4xl font-bold text-color-1">{totalReviews}+</div>
                  <div className="text-sm text-n-5 font-kufi font-light dark:text-n-4 mt-1">{t("happy customers")}</div>
                </motion.div>
                
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  transition={{ delay: 0.4, type: "spring" }}
                  className="text-center"
                >
                  <div className="flex items-center gap-1 text-4xl font-bold text-color-1">
                    {averageRating}
                    <FiStar className="text-yellow-400" size={28} />
                  </div>
                  <div className="text-sm text-n-5 font-kufi font-light dark:text-n-4 mt-1">{t("average rating")}</div>
                </motion.div>
                
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0, opacity: 0 }}
                  transition={{ delay: 0.5, type: "spring" }}
                  className="text-center"
                >
                  <div className="text-4xl font-bold text-color-1">100%</div>
                  <div className="text-sm text-n-5 font-kufi font-light dark:text-n-4 mt-1">{t("recommended")}</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Swiper Carousel with 3D Effect */}
            <div className="relative max-w-6xl mx-auto">
              {/* Navigation Buttons */}
              <button
                className="custom-swiper-button-prev absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white dark:bg-n-8 rounded-full shadow-lg flex items-center justify-center hover:bg-color-1 hover:text-white transition-all duration-300 -ml-6 lg:-ml-12"
                aria-label="Previous review"
              >
                <FiChevronLeft size={24} />
              </button>
              <button
                className="custom-swiper-button-next absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white dark:bg-n-8 rounded-full shadow-lg flex items-center justify-center hover:bg-color-1 hover:text-white transition-all duration-300 -mr-6 lg:-mr-12"
                aria-label="Next review"
              >
                <FiChevronRight size={24} />
              </button>

              <Swiper
                effect={'cards'}
                grabCursor={true}
                centeredSlides={true}
                slidesPerView={1}
                spaceBetween={30}
                breakpoints={{
                  640: {
                    slidesPerView: 1,
                    spaceBetween: 20,
                  },
                  768: {
                    slidesPerView: 2,
                    spaceBetween: 30,
                  },
                  1024: {
                    slidesPerView: 3,
                    spaceBetween: 30,
                  },
                }}
                pagination={{
                  clickable: true,
                  dynamicBullets: true,
                }}
                navigation={{
                  prevEl: '.custom-swiper-button-prev',
                  nextEl: '.custom-swiper-button-next',
                }}
                modules={[EffectCards, Pagination, Navigation, Autoplay]}
                className="reviews-swiper !py-12"
                autoplay={{
                  delay: 4000,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                loop={true}
                onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
              >
                {AllReviews?.map((review, index) => (
                  <SwiperSlide key={review._id || index}>
                    <ReviewsCard
                      reviews={review} 
                      isActive={activeIndex === index}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Trust Badge */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-16"
            >
              <div className="inline-flex items-center gap-4 px-6 py-3 bg-white dark:bg-n-8 rounded-full shadow-md">
                <div className="flex -space-x-2">
                  {AllReviews?.slice(0, 4).map((review, idx) => (
                    <img
                      key={idx}
                      src={`${media}${review?.Image}`}
                      alt={review?.titleEn}
                      className="w-8 h-8 rounded-full border-2 border-white dark:border-n-8 object-cover"
                    />
                  ))}
                </div>
                <span className="text-sm text-n-6 font-kufi font-light dark:text-n-3">
                  {t("trusted by thousands of customers")}
                </span>
              </div>
            </motion.div>
          </div>
        </Section>
      )}
    </div>
  );
}

export default Reviews;